import React, { useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import membershipIconSrc from '@assets/images/Icons/Membership.svg'
import {
  Avatar,
  Badge,
  Breadcrumbs,
  Button,
  DataTable,
  SectionCard,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import {
  buildMembershipMemberDetail,
  MEMBERSHIP_DETAIL_PAGE_SIZE,
  MEMBERSHIP_DETAIL_PAGE_SIZE_OPTIONS,
  MEMBERSHIP_STATUS_BADGE_COLOR,
  membershipTierLabel,
} from '@constants/Membership/membershipPage.constants'
import type {
  MembershipBenefitClaim,
  MembershipPageMember,
  MembershipViewSource,
  MembershipPaymentHistoryRow,
  MembershipReactivationHistoryRow,
} from '@dataTypes/membership.type'
import { getMembershipMember } from '@store/actions/membership.action'
import { useAppDispatch } from '@store/hooks'
import type { PatientProfilePreviousPage } from '@utils/patientProfileNavigation.util'
import MembershipUsageTile, {
  getMembershipUsagePercent,
} from './MembershipUsageTile.component'
import CancelMembershipModal from './CancelMembershipModal.component'
import TemporaryHoldModal from './TemporaryHoldModal.component'
import ReactivateMembershipModal from './ReactivateMembershipModal.component'
import ManageRecurringPaymentModal from './ManageRecurringPaymentModal.component'
import UpdateMembershipPlanModal from './UpdateMembershipPlanModal.component'
import './Membership.css'
import './MembershipMemberDetail.css'

const MEMBERSHIP_ICON_STYLE = {
  '--membership-icon': `url(${membershipIconSrc})`,
} as React.CSSProperties

const compareTableValues = (
  left: string | number,
  right: string | number,
): number => {
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

const parseTableDate = (value: string): number => {
  const timestamp = Date.parse(value)
  return Number.isNaN(timestamp) ? 0 : timestamp
}

const parseTableAmount = (value: string): number => {
  const amount = Number(value.replace(/[^0-9.-]/g, ''))
  return Number.isNaN(amount) ? 0 : amount
}

const getBenefitSortValue = (
  row: MembershipBenefitClaim,
  columnId: string,
): string | number => {
  if (columnId === 'date') return parseTableDate(row.date)
  if (columnId === 'amount') return parseTableAmount(row.amount)
  if (columnId === 'procedure') return row.procedure
  return ''
}

const getPaymentSortValue = (
  row: MembershipPaymentHistoryRow,
  columnId: string,
): string | number => {
  if (columnId === 'paymentDate') return parseTableDate(row.paymentDate)
  if (columnId === 'paymentMethod') return row.paymentMethod
  if (columnId === 'paymentType') return row.paymentType
  if (columnId === 'paymentStatus') return row.paymentStatus
  return ''
}

const getReactivationSortValue = (
  row: MembershipReactivationHistoryRow,
  columnId: string,
): string | number => {
  if (columnId === 'date') return parseTableDate(row.date)
  if (columnId === 'type') return row.type
  if (columnId === 'reason') return row.reason
  return ''
}

const useSortableRows = <T,>(
  rows: T[],
  getSortValue: (row: T, columnId: string) => string | number,
): {
  sortedRows: T[]
  sortColumnId: string | undefined
  sortDirection: DataTableSortDirection
  handleSort: (columnId: string) => void
} => {
  const [sortColumnId, setSortColumnId] = useState<string | undefined>()
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')

  const sortedRows = useMemo(() => {
    if (!sortColumnId) return rows

    const direction = sortDirection === 'desc' ? -1 : 1

    return [...rows].sort((left, right) => {
      const leftValue = getSortValue(left, sortColumnId)
      const rightValue = getSortValue(right, sortColumnId)
      return compareTableValues(leftValue, rightValue) * direction
    })
  }, [getSortValue, rows, sortColumnId, sortDirection])

  const handleSort = (columnId: string): void => {
    if (sortColumnId === columnId) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }

    setSortColumnId(columnId)
    setSortDirection('asc')
  }

  return { sortedRows, sortColumnId, sortDirection, handleSort }
}

const MembershipMemberDetail: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { memberId } = useParams<{ memberId: string }>()
  const locationState = location.state as {
    member?: MembershipViewSource
    previousPage?: PatientProfilePreviousPage
  } | null
  const routedMember = locationState?.member
  const stateMember = routedMember?.id === memberId ? routedMember : undefined
  const [loadedMember, setLoadedMember] = useState<
    MembershipViewSource | undefined
  >(stateMember)
  const [memberRequestSettled, setMemberRequestSettled] = useState(!memberId)

  useEffect(() => {
    if (!memberId) return

    let cancelled = false
    void dispatch(getMembershipMember(memberId)).then((result) => {
      if (cancelled) return
      if (getMembershipMember.fulfilled.match(result)) {
        setLoadedMember(result.payload)
      }
      setMemberRequestSettled(true)
    })

    return () => {
      cancelled = true
    }
  }, [dispatch, memberId])

  const refreshLoadedMember = (): void => {
    if (!memberId) return

    void dispatch(getMembershipMember(memberId)).then((result) => {
      if (getMembershipMember.fulfilled.match(result)) {
        setLoadedMember(result.payload)
      }
    })
  }

  const keepMemberCards = (updated: MembershipViewSource): void => {
    setLoadedMember((current) => ({
      ...(current ?? updated),
      ...updated,
    }))
    refreshLoadedMember()
  }

  const sourceMember = loadedMember ?? stateMember
  const member = sourceMember
    ? buildMembershipMemberDetail(sourceMember)
    : undefined
  const previousPage = locationState?.previousPage
  const fromPatientProfile = Boolean(
    previousPage?.path && previousPage.backLabel,
  )

  const [paymentPage, setPaymentPage] = useState(1)
  const [paymentPageSize, setPaymentPageSize] = useState(
    MEMBERSHIP_DETAIL_PAGE_SIZE,
  )
  const [reactivationPage, setReactivationPage] = useState(1)
  const [reactivationPageSize, setReactivationPageSize] = useState(
    MEMBERSHIP_DETAIL_PAGE_SIZE,
  )
  const [isCancelOpen, setIsCancelOpen] = useState(false)
  const [isHoldOpen, setIsHoldOpen] = useState(false)
  const [isReactivateOpen, setIsReactivateOpen] = useState(false)
  const [heldMemberId, setHeldMemberId] = useState<string | null>(null)
  const [holdStartedAt, setHoldStartedAt] = useState<Date | null>(null)
  const [isUpdateOpen, setIsUpdateOpen] = useState(false)
  const [isManagePaymentOpen, setIsManagePaymentOpen] = useState(false)
  const [paymentDetails, setPaymentDetails] = useState<{
    memberId: string
    billingLabel: string
    autoPayMethod: string
  } | null>(null)

  const benefitSort = useSortableRows(
    member?.benefitClaims ?? [],
    getBenefitSortValue,
  )
  const paymentSort = useSortableRows(
    member?.paymentHistory ?? [],
    getPaymentSortValue,
  )
  const reactivationSort = useSortableRows(
    member?.reactivationHistory ?? [],
    getReactivationSortValue,
  )

  const paymentTotalPages = Math.max(
    1,
    Math.ceil(paymentSort.sortedRows.length / paymentPageSize),
  )
  const safePaymentPage = Math.min(paymentPage, paymentTotalPages)
  const pagedPayments = useMemo(() => {
    const start = (safePaymentPage - 1) * paymentPageSize
    return paymentSort.sortedRows.slice(start, start + paymentPageSize)
  }, [paymentPageSize, paymentSort.sortedRows, safePaymentPage])

  const reactivationTotalPages = Math.max(
    1,
    Math.ceil(reactivationSort.sortedRows.length / reactivationPageSize),
  )
  const safeReactivationPage = Math.min(
    reactivationPage,
    reactivationTotalPages,
  )
  const pagedReactivations = useMemo(() => {
    const start = (safeReactivationPage - 1) * reactivationPageSize
    return reactivationSort.sortedRows.slice(
      start,
      start + reactivationPageSize,
    )
  }, [reactivationPageSize, reactivationSort.sortedRows, safeReactivationPage])

  const benefitColumns: DataTableColumn<MembershipBenefitClaim>[] = useMemo(
    () => [
      { id: 'date', label: 'Date', sortable: true },
      { id: 'procedure', label: 'Procedure', sortable: true },
      { id: 'amount', label: 'Amount availed', sortable: true },
    ],
    [],
  )

  const paymentColumns: DataTableColumn<MembershipPaymentHistoryRow>[] =
    useMemo(
      () => [
        { id: 'paymentDate', label: 'Payment date', sortable: true },
        { id: 'paymentMethod', label: 'Payment method', sortable: true },
        { id: 'paymentType', label: 'Payment type', sortable: true },
        {
          id: 'paymentStatus',
          label: 'Payment status',
          sortable: true,
          renderCell: (row) => (
            <Badge
              mode="tinted"
              color="vivid-green"
              label={row.paymentStatus}
            />
          ),
        },
      ],
      [],
    )

  const reactivationColumns: DataTableColumn<MembershipReactivationHistoryRow>[] =
    useMemo(
      () => [
        { id: 'date', label: 'Date', sortable: true },
        { id: 'type', label: 'Type', sortable: true },
        { id: 'reason', label: 'Reason', sortable: true },
      ],
      [],
    )

  const handlePaymentPageSizeChange = (nextPageSize: number): void => {
    setPaymentPageSize(nextPageSize)
    setPaymentPage(1)
  }

  const handleReactivationPageSizeChange = (nextPageSize: number): void => {
    setReactivationPageSize(nextPageSize)
    setReactivationPage(1)
  }

  const goToPatientProfile = (): void => {
    if (!previousPage?.path) return
    navigate(previousPage.path, {
      state: previousPage.profileTab
        ? { profileTab: previousPage.profileTab }
        : undefined,
    })
  }

  const goToMembership = (): void => {
    navigate('/membership')
  }

  const goToBenefits = (): void => {
    if (!memberId || !member) return
    navigate(`/membership/${memberId}/benefits`, {
      state: { ...(locationState ?? {}), member: sourceMember ?? member },
    })
  }

  const handleOpenCancelMembership = (): void => {
    setIsCancelOpen(true)
  }

  const handleCloseCancelMembership = (): void => {
    setIsCancelOpen(false)
  }

  const handleMembershipCanceled = (): void => {
    navigate('/membership')
  }

  const handleOpenTemporaryHold = (): void => {
    setIsHoldOpen(true)
  }

  const handleCloseTemporaryHold = (): void => {
    setIsHoldOpen(false)
  }

  const handleMembershipHeld = (updated: MembershipPageMember): void => {
    keepMemberCards(updated)
    setHeldMemberId(updated.id)
    setHoldStartedAt(new Date())
  }

  const handleOpenReactivate = (): void => {
    setIsReactivateOpen(true)
  }

  const handleCloseReactivate = (): void => {
    setIsReactivateOpen(false)
  }

  const handleMembershipReactivated = (updated: MembershipPageMember): void => {
    keepMemberCards(updated)
    setHeldMemberId(null)
    setHoldStartedAt(null)
  }

  const handleOpenUpdatePlan = (): void => {
    setIsUpdateOpen(true)
  }

  const handleCloseUpdatePlan = (): void => {
    setIsUpdateOpen(false)
  }

  const handleMembershipUpdated = (updated?: MembershipViewSource): void => {
    setIsUpdateOpen(false)
    if (updated) {
      keepMemberCards(updated)
      return
    }
    refreshLoadedMember()
  }

  const handleOpenManagePayment = (): void => {
    setIsManagePaymentOpen(true)
  }

  const handleCloseManagePayment = (): void => {
    setIsManagePaymentOpen(false)
  }

  const handlePaymentDetailsSaved = (details: {
    member: MembershipPageMember
    billingLabel: string
    autoPayMethod: string
  }): void => {
    keepMemberCards(details.member)
    setPaymentDetails({
      memberId: details.member.id,
      billingLabel: details.billingLabel,
      autoPayMethod: details.autoPayMethod,
    })
  }

  if (!memberId || (memberRequestSettled && !member)) {
    return <Navigate to="/membership" replace />
  }

  if (!member) return null

  const billingLabel =
    paymentDetails?.memberId === member.id
      ? paymentDetails.billingLabel
      : member.billingLabel
  const autoPayMethod =
    paymentDetails?.memberId === member.id
      ? paymentDetails.autoPayMethod
      : member.autoPayMethod
  const isOnHold = member.status === 'On hold' || heldMemberId === member.id

  return (
    <div className="membership-page membership-detail">
      <header className="membership-page__header membership-page__header--start">
        <div className="membership-page__header-start">
          <Breadcrumbs
            className={`membership-page__breadcrumbs${
              fromPatientProfile ? ' membership-page__breadcrumbs--profile' : ''
            }`}
            style={{ margin: 0, justifyContent: 'flex-start' }}
            items={
              fromPatientProfile && previousPage
                ? [
                    {
                      label: previousPage.backLabel,
                      onClick: goToPatientProfile,
                    },
                    { label: 'Membership', onClick: goToMembership },
                    { label: member.name },
                  ]
                : [
                    { label: 'Membership', onClick: goToMembership },
                    { label: member.name },
                  ]
            }
            onBackClick={
              fromPatientProfile ? goToPatientProfile : goToMembership
            }
          />
        </div>
      </header>
      <div className="membership-detail__body">
        <SectionCard
          className="membership-detail__card"
          title="Overview"
          showTitleChevron={false}
        >
          <div className="membership-detail__person">
            <div className="membership-detail__person-start">
              <Avatar name={member.name} size={40} color="blue" />
              <div className="membership-detail__person-copy">
                <p className="membership-detail__person-name">{member.name}</p>
                <p className="membership-detail__person-role">{member.role}</p>
              </div>
            </div>
            <div className="membership-detail__person-end">
              <span className="membership-page__tier">
                <span
                  className={`membership-page__tier-icon membership-page__tier-icon--${member.tier} membership-detail__tier-icon`}
                  style={MEMBERSHIP_ICON_STYLE}
                  aria-hidden
                />
                <span className="membership-detail__tier-label">
                  {membershipTierLabel(member)} membership
                </span>
              </span>
              <Badge
                mode="tinted"
                color={MEMBERSHIP_STATUS_BADGE_COLOR[member.status]}
                label={member.status}
              />
            </div>
          </div>
          <div className="membership-detail__fields">
            <div className="membership-detail__field">
              <p className="membership-detail__field-value">
                {member.memberNumber}
              </p>
              <p className="membership-detail__field-label">Member ID</p>
            </div>
            <div className="membership-detail__field">
              <p className="membership-detail__field-value">
                {member.memberSinceLabel}
              </p>
              <p className="membership-detail__field-label">Member since</p>
            </div>
            <div className="membership-detail__field">
              <div className="membership-detail__field-heading">
                <p className="membership-detail__field-value">
                  {member.nextPaymentDate}
                </p>
                {isOnHold ? (
                  <Badge mode="tinted" color="orange" label="On-hold" />
                ) : null}
              </div>
              <p className="membership-detail__field-label">
                {isOnHold
                  ? 'Auto-payments will resume after reactivation.'
                  : 'Next payment date'}
              </p>
            </div>
            <div className="membership-detail__field">
              <p className="membership-detail__field-value">{billingLabel}</p>
              <p className="membership-detail__field-label">
                Billing frequency
              </p>
            </div>
            <div className="membership-detail__field">
              <p className="membership-detail__field-value">{autoPayMethod}</p>
              <p className="membership-detail__field-label">Auto-pay method</p>
            </div>
            <div className="membership-detail__field">
              <p className="membership-detail__field-value">
                {member.notificationChannels}
              </p>
              <p className="membership-detail__field-label">
                Notification channels
              </p>
            </div>
          </div>
          <div className="membership-detail__actions">
            <Button
              variant="secondary"
              size="md"
              destructive
              onClick={handleOpenCancelMembership}
            >
              Cancel membership
            </Button>
            <div className="membership-detail__actions-end">
              <Button
                variant="secondary"
                size="md"
                disabled={member.disableEdit}
                onClick={handleOpenUpdatePlan}
              >
                Edit membership
              </Button>
              {isOnHold ? (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleOpenReactivate}
                >
                  Reactivate
                </Button>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={handleOpenTemporaryHold}
                  >
                    Temporary hold
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleOpenManagePayment}
                  >
                    Manage payments
                  </Button>
                </>
              )}
            </div>
          </div>
        </SectionCard>
        <SectionCard
          className="membership-detail__card membership-detail__card--benefits"
          showTitleChevron={false}
          headerAfterTitle={
            <button
              type="button"
              className="membership-detail__benefits-heading"
              onClick={goToBenefits}
            >
              <span>Benefits usage tracking</span>
              <i
                className="mgc mgc_right_line membership-detail__title-chevron"
                aria-hidden
              />
            </button>
          }
        >
          <div className="membership-detail__usage-grid">
            {member.benefitUsage.map((item) => (
              <MembershipUsageTile
                key={item.id}
                label={item.label}
                value={`${item.used}/${item.total} used`}
                percent={getMembershipUsagePercent(item.used, item.total)}
              />
            ))}
            <MembershipUsageTile
              label="Total availed discounts"
              value={member.totalAvailedDiscounts}
              percent={member.totalAvailedDiscountsPercent}
            />
          </div>
          <DataTable
            className="membership-detail__table"
            columns={benefitColumns}
            rows={benefitSort.sortedRows}
            showActions={false}
            sortColumnId={benefitSort.sortColumnId}
            sortDirection={benefitSort.sortDirection}
            onSort={benefitSort.handleSort}
          />
        </SectionCard>
        <SectionCard
          className="membership-detail__card membership-detail__card--fill"
          title="Payment history"
          showTitleChevron={false}
        >
          <DataTable
            className="membership-detail__table"
            columns={paymentColumns}
            rows={pagedPayments}
            showActions={false}
            sortColumnId={paymentSort.sortColumnId}
            sortDirection={paymentSort.sortDirection}
            onSort={paymentSort.handleSort}
            pagination={{
              currentPage: safePaymentPage,
              totalPages: paymentTotalPages,
              pageSize: paymentPageSize,
              pageSizeOptions: MEMBERSHIP_DETAIL_PAGE_SIZE_OPTIONS,
              onPageChange: setPaymentPage,
              onPageSizeChange: handlePaymentPageSizeChange,
            }}
          />
        </SectionCard>
        <SectionCard
          className="membership-detail__card membership-detail__card--fill"
          title="Reactivation history"
          showTitleChevron={false}
        >
          <DataTable
            className="membership-detail__table"
            columns={reactivationColumns}
            rows={pagedReactivations}
            showActions={false}
            sortColumnId={reactivationSort.sortColumnId}
            sortDirection={reactivationSort.sortDirection}
            onSort={reactivationSort.handleSort}
            pagination={{
              currentPage: safeReactivationPage,
              totalPages: reactivationTotalPages,
              pageSize: reactivationPageSize,
              pageSizeOptions: MEMBERSHIP_DETAIL_PAGE_SIZE_OPTIONS,
              onPageChange: setReactivationPage,
              onPageSizeChange: handleReactivationPageSizeChange,
            }}
          />
        </SectionCard>
      </div>
      <CancelMembershipModal
        open={isCancelOpen}
        member={member}
        onClose={handleCloseCancelMembership}
        onCanceled={handleMembershipCanceled}
      />
      <TemporaryHoldModal
        open={isHoldOpen}
        member={member}
        onClose={handleCloseTemporaryHold}
        onHeld={handleMembershipHeld}
      />
      <ReactivateMembershipModal
        open={isReactivateOpen}
        member={member}
        holdStartedAt={holdStartedAt}
        onClose={handleCloseReactivate}
        onReactivated={handleMembershipReactivated}
      />
      <ManageRecurringPaymentModal
        open={isManagePaymentOpen}
        member={member}
        onClose={handleCloseManagePayment}
        onSaved={handlePaymentDetailsSaved}
      />
      <UpdateMembershipPlanModal
        open={isUpdateOpen}
        member={sourceMember}
        onClose={handleCloseUpdatePlan}
        onUpdated={handleMembershipUpdated}
      />
    </div>
  )
}

export default MembershipMemberDetail
